import DatePicker from "@/components/date-picker";
import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { InputField } from "@/components/input-field/InputField";
import { CustomPopover } from "@/components/popover/CustomPopover";
import { SelectComponent } from "@/components/select/Select";
import { CheckRequestStatus, RequestTypes } from "@/constants/entity-status";
import { useAuth } from "@/hooks/useAuth";
import { getSucursales, Sucursal } from "@/services/accounts.service";
import { AppColors } from "@/styles/colors";
import { isEmpty } from "@/utils/query-string";
import {
  Badge,
  Box,
  Button,
  Field,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { Filter, SearchIcon } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Control, FieldValues, UseFormHandleSubmit } from "react-hook-form";

interface Props {
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  openFilters: boolean;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => void;
  clearFilters: () => void;
  control: Control<FieldValues>;
  disableFilters?: boolean;
  loadingFilters?: boolean;
  filtersApplied?: boolean;
  disableClear?: boolean;
  loadingOptions?: boolean;
}

export const CheckRequestFilter = ({
  openFilters,
  disableFilters,
  loadingFilters,
  filtersApplied,
  disableClear,
  control,
  loadingOptions,
  submitFilters,
  handleSubmit,
  setOpenFilters,
  //handleSearch,
  clearFilters,
}: Props) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { user } = useAuth();

  const [loadingSucursales, setLoadingSucursales] = useState<boolean>(false);

  const [sucursales, setSucursales] = useState<Sucursal[] | null>([]);

  const fetchSucursales = async () => {
    setLoadingSucursales(true);

    const response = await getSucursales({
      usuario: user?.localAccountId ?? "",
    });
    setSucursales(response);

    setLoadingSucursales(false);
  };

  useEffect(() => {
    if (isActive && !isEmpty(user?.localAccountId)) {
      fetchSucursales();
    }
  }, [isActive, user?.localAccountId]);

  return (
    <Flex width="100%" paddingY="10px" justifyContent="flex-end">
      <HStack gap="10">
        <CustomPopover
          open={openFilters}
          onOpenChange={(e) => setOpenFilters(e.open)}
          triggerComponent={
            <Box position="relative" display="inline-block">
              <Button
                variant="outline"
                paddingX="12px"
                height="32px"
                disabled={disableFilters || loadingFilters}
              >
                <Filter />
                <Text fontWeight={600} fontSize="14px">
                  Filtrar
                </Text>
              </Button>
              {filtersApplied && (
                <Badge
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  borderRadius="full"
                  px="2"
                  py="0.5"
                >
                  !
                </Badge>
              )}
            </Box>
          }
          bodyComponent={
            <Flex
              as="form"
              flexDir="column"
              padding="25px"
              gap="15px"
              onSubmit={handleSubmit(submitFilters)}
            >
              <Flex alignItems={"center"} justifyContent="space-between">
                <Text fontWeight={600} fontSize="20px">
                  Filtrar
                </Text>

                <Button
                  variant="plain"
                  color={AppColors.BLUE_TEXT}
                  fontWeight={600}
                  onClick={clearFilters}
                  disabled={loadingFilters || disableClear}
                  loading={loadingFilters}
                >
                  Limpiar filtros
                </Button>
              </Flex>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <DatePicker
                    control={control}
                    labelText="Fecha"
                    name="request_date"
                    placeholder="Ingresar fecha"
                    maxDate={dayjs()}
                  />
                </GridItem>
                <GridItem>
                  <InputField
                    inputName="account_owner"
                    labelText="Titular"
                    placeholder="Ingresar titular de la cuenta"
                    control={control}
                    startElement={
                      <SearchIcon
                        size="15px"
                        style={{ marginLeft: "12px", marginTop: "2px" }}
                      />
                    }
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>
                <GridItem>
                  <InputField
                    inputName="account_number"
                    labelText="N° Cuenta"
                    placeholder="Ingresar N° Cuenta"
                    control={control}
                    startElement={
                      <SearchIcon
                        size="15px"
                        style={{ marginLeft: "12px", marginTop: "2px" }}
                      />
                    }
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>
                <GridItem>
                  <SelectComponent
                    name="sucursal"
                    items={
                      sucursales?.map((status) => ({
                        label: status?.nombre,
                        value: status?.clave,
                      })) ?? []
                    }
                    labelText="Sucursal"
                    placeholder="Seleccionar una opción"
                    control={control}
                    loading={loadingSucursales}
                    disabled={loadingSucursales}
                    multiple
                    menuMaxHeight="250px"
                  />
                </GridItem>
                <GridItem>
                  <Field.Root>
                    <Field.Label>Monto</Field.Label>
                    <InputCurrency
                      name="request_amount"
                      placeholder="Ingresar monto"
                      control={control}
                      maxLength={20}
                      disabled={disableFilters || loadingFilters}
                    />
                  </Field.Root>
                </GridItem>
                <GridItem>
                  <InputField
                    inputName="requester"
                    labelText="Solicitante"
                    placeholder="Ingresar solicitante"
                    control={control}
                    startElement={
                      <SearchIcon
                        size="15px"
                        style={{ marginLeft: "12px", marginTop: "2px" }}
                      />
                    }
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>
                <GridItem>
                  <SelectComponent
                    name="request_type"
                    items={[
                      {
                        label: "Cheque Físico",
                        value: RequestTypes.PHYSICAL_CHECK,
                      },
                      {
                        label: "E-Cheq",
                        value: RequestTypes.E_CHEQ,
                      },
                    ]?.map((status) => ({
                      label: status?.label,
                      value: status?.value,
                    }))}
                    labelText="Tipo de cheque"
                    placeholder="Seleccionar una opción"
                    control={control}
                    loading={loadingOptions}
                  />
                </GridItem>
                <GridItem>
                  <SelectComponent
                    name="estado"
                    items={Object.values(CheckRequestStatus)?.map((status) => ({
                      label: status,
                      value: status,
                    }))}
                    labelText="Estado"
                    placeholder="Seleccionar una opción"
                    control={control}
                    loading={loadingOptions}
                    multiple
                    menuMaxHeight="250px"
                  />
                </GridItem>
              </Grid>

              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                type="submit"
                loading={loadingFilters}
                disabled={loadingFilters || disableFilters}
              >
                Aplicar filtros
              </Button>
            </Flex>
          }
        />
      </HStack>
    </Flex>
  );
};
